'use client'

import { LifeBuoyIcon, MessageSquareTextIcon } from 'lucide-react'
import { useTranslations } from 'next-intl'

import { RailTooltip } from '@/components/navigation/app-side-panel'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  ConnectionDot,
  ConnectionLabel,
  ConnectionStatusHoverCard,
  useAppConnection,
} from '@/components/widget/connection-status'
import { openSupportChat, SOCIAL_LINKS } from '@/components/widget/support-links'
import { cn } from '@/lib/utils'
import { useConnectedVenueIssues } from '@/service/venue-trading-status.hook'

/**
 * The side panel's connection row (moved up from the status bar): a status light on the rail,
 * "Connected · 23ms" when expanded. Hovering either opens the per-venue breakdown to the right.
 */
export function SidePanelConnectionStatus({
  collapsed,
  collapsedClassName,
  expandedClassName,
}: {
  collapsed: boolean
  collapsedClassName: string
  expandedClassName: string
}) {
  const t = useTranslations('trade')
  const { isConnected, latencyMs } = useAppConnection()
  const venueIssues = useConnectedVenueIssues()

  return (
    <ConnectionStatusHoverCard isConnected={isConnected} side="right" align="end">
      <button
        type="button"
        aria-label={t('connectionStatus')}
        className={cn(collapsed ? collapsedClassName : expandedClassName, 'cursor-default')}
      >
        {/* Same 16px slot as the other footer icons, so the light lines up with them. */}
        <span className="flex size-4 shrink-0 items-center justify-center">
          <ConnectionDot isConnected={isConnected} issues={venueIssues} className="size-2" />
        </span>
        {!collapsed && (
          <span className="min-w-0 truncate text-xs">
            <ConnectionLabel isConnected={isConnected} latencyMs={latencyMs} issues={venueIssues} />
          </span>
        )}
      </button>
    </ConnectionStatusHoverCard>
  )
}

/**
 * "Need support?" in the side panel (moved up from the status bar). Expanded: a small card with
 * Contact support plus the X and LinkedIn links. On the rail: one life-buoy button opening the
 * same three as a menu to the right.
 */
export function SidePanelSupport({
  collapsed,
  collapsedClassName,
}: {
  collapsed: boolean
  collapsedClassName: string
}) {
  const t = useTranslations('trade')

  if (collapsed) {
    return (
      <DropdownMenu>
        <RailTooltip label={t('needSupport')}>
          <DropdownMenuTrigger asChild>
            <button type="button" aria-label={t('needSupport')} className={collapsedClassName}>
              <LifeBuoyIcon className="text-orderx-button-mint size-4" />
            </button>
          </DropdownMenuTrigger>
        </RailTooltip>
        <DropdownMenuContent side="right" align="end" sideOffset={8} className="z-[70] min-w-48">
          <DropdownMenuItem onSelect={openSupportChat}>
            <MessageSquareTextIcon className="size-4 shrink-0" />
            {t('contactSupport')}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          {SOCIAL_LINKS.map(({ id, label, href, icon: Icon }) => (
            <DropdownMenuItem key={id} asChild>
              <a href={href} target="_blank" rel="noopener noreferrer">
                <Icon className="size-4 shrink-0" />
                {label}
              </a>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    )
  }

  return (
    <div className="bg-foreground/5 flex flex-col gap-2 rounded-sm p-2.5">
      <span className="text-foreground/60 text-xs">{t('needSupport')}</span>
      <div className="flex items-center gap-1">
        <Button
          variant="secondary"
          size="xs"
          onClick={openSupportChat}
          className="min-w-0 flex-1 motion-safe:active:scale-[0.97]"
        >
          <MessageSquareTextIcon className="size-3.5" />
          <span className="truncate">{t('contactSupport')}</span>
        </Button>
        {SOCIAL_LINKS.map(({ id, label, href, icon: Icon }) => (
          <Button
            key={id}
            asChild
            variant="subtle"
            size="iconXs"
            className="motion-safe:active:scale-[0.94]"
          >
            <a href={href} target="_blank" rel="noopener noreferrer" aria-label={label}>
              <Icon className="size-3.5" />
            </a>
          </Button>
        ))}
      </div>
    </div>
  )
}

/** The mobile nav sheet's versions: an inline status row and a support row, no hover card. */
export function MobileNavConnectionStatus({ className }: { className: string }) {
  const { isConnected, latencyMs } = useAppConnection()
  const venueIssues = useConnectedVenueIssues()
  return (
    <div className={cn(className, 'cursor-default')}>
      <span className="flex size-4 shrink-0 items-center justify-center">
        <ConnectionDot isConnected={isConnected} issues={venueIssues} className="size-2" />
      </span>
      <span className="min-w-0 flex-1 truncate text-left text-sm">
        <ConnectionLabel isConnected={isConnected} latencyMs={latencyMs} issues={venueIssues} />
      </span>
    </div>
  )
}

export function MobileNavSupport({ className }: { className: string }) {
  const t = useTranslations('trade')
  return (
    <div className="flex items-center gap-1 pr-3">
      <button type="button" onClick={openSupportChat} className={cn(className, 'min-w-0 flex-1')}>
        <LifeBuoyIcon className="size-4 shrink-0" />
        <span className="min-w-0 flex-1 truncate text-left">{t('needSupport')}</span>
      </button>
      {SOCIAL_LINKS.map(({ id, label, href, icon: Icon }) => (
        <Button key={id} asChild variant="subtle" size="iconXs">
          <a href={href} target="_blank" rel="noopener noreferrer" aria-label={label}>
            <Icon className="size-3.5" />
          </a>
        </Button>
      ))}
    </div>
  )
}
